import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  createNgModule,
  inject,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatSidenav } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { AlloPanelService } from '@qtek/libs/ai-chat';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import {
  HealthcheckCoreService,
  QtNetError,
} from '@qtek/libs/healthcheck-core';
import { AuthStore, CoreStore, UserStore, WizardStore } from '@qtek/libs/store';
import {
  DynamicDrawer,
  OnlineService,
  RIGHT_SIDENAV_STATE,
} from '@qtek/shared/services';
import {
  IMPERSONATION_EMAIL_KEY,
  IMPERSONATION_TOKEN_KEY,
  collapseEnterLeaveAnimation,
  resolveImpersonationContext,
  setImpersonationDomState,
} from '@qtek/shared/utils';
import { Observable, Subject, combineLatest, from, of, timer } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { RightSidenavStateService } from '../../services/right-sidenav-state.service';
import { SsoWelcomeService } from '../../services/sso-welcome.service';
import { RightSidenavComponent } from '../right-sidenav/right-sidenav.component';

const EXPAND_ANIMATION = trigger('size', [
  state(
    '1',
    style({
      width: '220px',
    })
  ),
  state(
    '0',
    style({
      width: '70px',
    })
  ),
  state(
    'hidden',
    style({
      width: '0px',
      display: 'none',
    })
  ),
  transition('1 <=> 0', [animate('220ms cubic-bezier(0.4, 0, 0.2, 1)')]),
  transition('* <=> hidden', [
    animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
  ]),
]);

export const sideNavContainerAnimation = trigger('openCloseSidenavContent', [
  state(
    '1',
    style({
      'margin-left': '220px',
    })
  ),
  state(
    '0',
    style({
      'margin-left': '70px',
    })
  ),
  state(
    'mobile',
    style({
      'margin-left': '0',
    })
  ),
  state(
    'hidden',
    style({
      'margin-left': '0',
    })
  ),
  transition('1 <=> 0', [animate('220ms cubic-bezier(0.4, 0, 0.2, 1)')]),
  transition('* <=> hidden', [
    animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
  ]),
]);

@Component({
  selector: 'qt-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['./layout.component.scss'],
  /** Provide service directly to destroy with component */
  providers: [
    RightSidenavStateService,
    SsoWelcomeService,
    {
      provide: RIGHT_SIDENAV_STATE,
      useExisting: RightSidenavStateService,
    },
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    EXPAND_ANIMATION,
    sideNavContainerAnimation,
    collapseEnterLeaveAnimation,
  ],
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private document = inject<Document>(DOCUMENT);
  router = inject(Router);
  private injector = inject(Injector);
  private titleService = inject(Title);
  private online = inject(OnlineService);
  private wizardStore = inject(WizardStore);
  private coreStore = inject(CoreStore);
  private authStore = inject(AuthStore);
  private userStore = inject(UserStore);
  private dynamicDrawer = inject(DynamicDrawer);
  private observer = inject(BreakpointObserver);
  private healthCheck = inject(HealthcheckCoreService);
  private rightSidenavState = inject(RightSidenavStateService);
  private alloPanel = inject(AlloPanelService);
  private dialogLoader = inject(LazyDialogLoader);
  protected ssoWelcome = inject(SsoWelcomeService);

  mini$: Observable<boolean>;
  wizardOpened: boolean;
  destroy$ = new Subject<void>();
  networkError$: Observable<QtNetError | null> = of(null);
  mode$: Observable<'side' | 'over'>;
  mobile$: Observable<boolean>;
  isUploadRoute$: Observable<boolean>;
  readonly rightSidenavWidth = signal<number>(350);
  /** Tracks right panel open state so margin-right updates reliably with OnPush when panel closes. */
  readonly rightPanelOpened = signal<boolean>(false);
  get rightSidenavActiveTab(): 'chat' | 'tasks' | null {
    return this.rightSidenavState.activeTab();
  }
  set rightSidenavActiveTab(tab: 'chat' | 'tasks' | null) {
    this.rightSidenavState.setActiveTab(tab);
  }
  canShowAiChat = false;
  readonly isImpersonating = signal(false);
  impersonatedEmail$: Observable<string>;

  isLargeScreen$: Observable<boolean>;

  readonly SIDENAV_W = { expanded: 220, mini: 70 };

  /** Tracks left sidebar expanded (true) vs minimized (false) so it stays minimized with OnPush. */
  readonly leftSidenavExpanded = signal<boolean>(true);

  @ViewChild('childDiv', { static: true }) childDiv: ElementRef;
  @ViewChild('dynamicSidenav') dynamicSidenav: MatSidenav;
  @ViewChild(CdkPortalOutlet) outlet: CdkPortalOutlet;
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('rightSidenav') rightSidenav: MatSidenav;
  @ViewChild(RightSidenavComponent)
  rightSidenavComponent: RightSidenavComponent;

  ngOnInit() {
    this.ssoWelcome.init();

    // SD-2716: an in-page "Ask Allo" opens the same docked panel as the toolbar icon.
    // openRightSidenavTab (not toggle) so a second ask never closes the panel.
    this.alloPanel.openRequests$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openRightSidenavTab('chat'));

    const userState$ = combineLatest({
      person: toObservable(this.userStore.currentPerson, {
        injector: this.injector,
      }),
      company: toObservable(this.userStore.mainCompany, {
        injector: this.injector,
      }),
      userRelation: toObservable(this.userStore.userRelation, {
        injector: this.injector,
      }),
    });
    const impersonationContext$ = userState$.pipe(
      map(userState =>
        resolveImpersonationContext(
          userState,
          sessionStorage.getItem(IMPERSONATION_TOKEN_KEY),
          sessionStorage.getItem(IMPERSONATION_EMAIL_KEY)
        )
      ),
      tap(({ active, email }) => {
        if (active && email) {
          sessionStorage.setItem(IMPERSONATION_EMAIL_KEY, email);
        }
      }),
      shareReplay(1)
    );

    this.impersonatedEmail$ = impersonationContext$.pipe(
      map(({ email }) => email),
      distinctUntilChanged()
    );

    impersonationContext$
      .pipe(
        map(({ active }) => active),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(isActive => this.isImpersonating.set(isActive));

    this.isUploadRoute$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url.includes('/upload')),
      startWith(this.router.url.includes('/upload')),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.mobile$ = this.observer.observe(Breakpoints.XSmall).pipe(
      map(breakpoint => Boolean(breakpoint.matches)),
      distinctUntilChanged(),
      shareReplay()
    );

    this.isLargeScreen$ = this.observer
      .observe([Breakpoints.Large, Breakpoints.XLarge])
      .pipe(
        map(breakpoint => Boolean(breakpoint.matches)),
        distinctUntilChanged(),
        shareReplay()
      );

    this.mini$ = this.mobile$.pipe(
      map(isMobile => !isMobile),
      shareReplay(1)
    );

    this.startWizard();

    /** Update title to company name. */
    toObservable(this.userStore.mainCompany, { injector: this.injector })
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        if (company?.companyName) {
          this.titleService.setTitle(company.companyName);
        }
      });

    const onlineStatus$ = this.online.change$.pipe(
      startWith(this.online.isOnline()),
      shareReplay()
    );

    const httpHealthCheck$ = this.healthCheck.isHealthyHttpConnection$.pipe(
      distinctUntilChanged(),
      switchMap(value => {
        if (value) {
          return of(value);
        }
        return timer(2000).pipe(map(() => value));
      })
    );

    const websocketHealthCheck$ = this.healthCheck.isWebSocketAlive$.pipe(
      distinctUntilChanged(),
      switchMap(value => {
        if (value) {
          return of(value);
        }
        return timer(5000).pipe(map(() => value));
      })
    );

    this.networkError$ = combineLatest([
      onlineStatus$,
      httpHealthCheck$,
      websocketHealthCheck$,
    ]).pipe(
      map(([isOnline, isHttpHealthy, isWsHealthy]) => {
        if (!isOnline) {
          return 'offline';
        }
        if (!isHttpHealthy) {
          return 'http';
        }
        if (!isWsHealthy) {
          return 'ws';
        }
        return null;
      }),
      distinctUntilChanged()
    );

    this.mode$ = this.mini$.pipe(map(bool => (bool ? 'side' : 'over')));
  }

  ngAfterViewInit() {
    this.dynamicDrawer.register({
      matDrawer: this.dynamicSidenav,
      cdkPortalOutlet: this.outlet,
    });

    const opened = this.rightSidenav?.opened ?? false;
    this.rightPanelOpened.set(opened);
    this.rightSidenavState.setOpened(opened);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleLeftSidenav(): void {
    this.leftSidenavExpanded.update(v => !v);
  }

  toggleRightSidenav() {
    if (this.rightSidenav) {
      const next = !this.rightSidenav.opened;
      this.rightPanelOpened.set(next);
      this.rightSidenavState.setOpened(next);
      this.rightSidenav.toggle().then(() => {
        const opened = this.rightSidenav.opened;
        this.rightPanelOpened.set(opened);
        this.rightSidenavState.setOpened(opened);
        if (opened) {
          const component = this.rightSidenavComponent;
          this.rightSidenavActiveTab = component
            ? component.selectedTab()
            : null;
        } else {
          this.rightSidenavActiveTab = null;
        }
      });
    }
  }

  /**
   * SD-3045: top-bar Allo button. Allo already open in its popout is brought to the front
   * instead, so it is not open in the popout and the docked panel at the same time.
   */
  openAiChat(): void {
    if (this.rightSidenavComponent?.focusChatPopout()) return;
    this.toggleRightSidenavTab('chat');
  }

  toggleRightSidenavTab(tab: 'chat' | 'tasks') {
    if (this.rightSidenav?.opened && this.rightSidenavActiveTab === tab) {
      this.rightPanelOpened.set(false);
      this.rightSidenavState.setOpened(false);
      this.rightSidenav.close();
      this.rightSidenavActiveTab = null;
      return;
    }
    this.openRightSidenavTab(tab);
  }

  openRightSidenavTab(tab: 'chat' | 'tasks'): boolean {
    const component = this.rightSidenavComponent;
    if (!component) {
      return false;
    }
    const canOpen = component.openTab(tab);
    if (!canOpen) {
      if (tab === 'chat') {
        return this.openRightSidenavTab('tasks');
      }
      return false;
    }
    this.rightSidenavActiveTab = tab;
    if (this.rightSidenav && !this.rightSidenav.opened) {
      this.rightPanelOpened.set(true);
      this.rightSidenavState.setOpened(true);
      this.rightSidenav.open();
    }
    return true;
  }

  onRightSidenavTabChange(tab: 'chat' | 'tasks') {
    this.rightSidenavActiveTab = tab;
  }

  onChatAvailabilityChange(canShow: boolean) {
    this.canShowAiChat = canShow;
    if (!canShow && this.rightSidenavActiveTab === 'chat') {
      this.rightSidenavActiveTab = null;
    }
  }

  onRightSidenavWidthChange(width: number) {
    this.rightSidenavWidth.set(width);
  }

  addNoteFromToolbar() {
    this.rightSidenavComponent?.addNote();
  }

  addContactFromToolbar() {
    this.rightSidenavComponent?.addContact();
  }

  addDealFromToolbar() {
    this.dialogLoader.open('select-deal-type').subscribe();
  }

  endImpersonationSession(): void {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_EMAIL_KEY);
    setImpersonationDomState(this.document, false);
    this.isImpersonating.set(false);
    this.authStore.logout({});
  }

  /**
   * Load wizard module and display dialog.
   */
  private openWizard() {
    return from(import('@qtek/libs/wizard')).pipe(
      mergeMap(({ WizardModule: WizardModule }) => {
        const moduleRef = createNgModule(WizardModule, this.injector);
        this.wizardOpened = true;
        return moduleRef.instance.openWizard().pipe(
          tap(() => {
            this.wizardOpened = false;
          })
        );
      })
    );
  }

  private startWizard() {
    this.wizardStore.stepsLoaded$
      .pipe(
        take(1),
        map(() => this.wizardStore.isWizardDone(true)),
        mergeMap((done: boolean) => {
          this.coreStore.closeLoading();

          /** Skip the onboarding wizard while impersonating: it's a
           *  full-screen dialog that would sit on top of the impersonation
           *  banner, leaving the admin with no way to reach "End Session". */
          const isImpersonating = Boolean(
            sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
          );

          if (!done && !isImpersonating) {
            return this.openWizard();
          } else {
            return of(null);
          }
        }),
        mergeMap(() => {
          return toObservable(this.userStore.userActions, {
            injector: this.injector,
          });
        }),
        filter(value => value && !this.wizardOpened),
        take(1),
        filter(res => res[0] != null && res[0].params != null),
        map(res => res[0].params),
        takeUntil(this.destroy$)
      )
      .subscribe();

    /** Load wizard steps */
    this.wizardStore.loadSteps();
  }
}
